/**
 * Error Logging Service — Wrapper del sistema centralizzato DeskbayLogger
 *
 * Mantiene la stessa API pubblica per compatibilità con ErrorBoundary e altri caller,
 * ma instrada tutto attraverso il logger centralizzato (app_logs, admin panel).
 */

import { logger } from '@deskbay/shared';

export type ErrorCategory = 'network' | 'auth' | 'api' | 'code' | 'unknown';

interface ErrorContext {
    userId?: string;
    page?: string;
    action?: string;
    component?: string;
    additionalData?: Record<string, unknown>;
}

/**
 * Categorize error based on message/type
 */
export function categorizeError(error: Error): ErrorCategory {
    const message = error.message.toLowerCase();

    if (message.includes('fetch') || message.includes('network') || message.includes('timeout')) {
        return 'network';
    }
    if (message.includes('unauthorized') || message.includes('auth') || message.includes('token')) {
        return 'auth';
    }
    if (message.includes('supabase') || message.includes('api') || message.includes('database')) {
        return 'api';
    }
    return 'code';
}

/**
 * Log error — routes to logger.error group (app_logs)
 */
export async function logError(
    error: Error,
    context: ErrorContext = {}
): Promise<void> {
    const category = categorizeError(error);
    const contextStr = context.component || context.page || context.action || 'errorLogger';

    logger.error.error(
        error.message,
        error,
        contextStr,
        {
            category,
            page: context.page,
            action: context.action,
            component: context.component,
            userId: context.userId,
            ...context.additionalData,
        }
    );
}

/**
 * Create error handler with context
 */
export function createErrorHandler(context: Partial<ErrorContext>) {
    return (error: Error) => {
        logError(error, context);
    };
}

/**
 * Wrap async function with error logging
 */
export function withErrorLogging<T extends (...args: unknown[]) => Promise<unknown>>(
    fn: T,
    context: ErrorContext
): T {
    return (async (...args: unknown[]) => {
        try {
            return await fn(...args);
        } catch (error) {
            if (error instanceof Error) {
                await logError(error, context);
            }
            throw error;
        }
    }) as T;
}

export const errorLogger = {
    log: logError,
    categorize: categorizeError,
    createHandler: createErrorHandler,
    wrap: withErrorLogging
};
